import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getSsoConfig, getSsoStartUrl } from '../../services/api';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #3b82f6 80%, #52acd0 100%);
`;

const LoginCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 420px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const LogoIcon = styled.div`
  width: 128px;
  height: 128px;
  background: linear-gradient(135deg, #2a2a2a 0%, #4a4a4a 20%, #1a1a1a 40%, #3a3a3a 60%, #252525 80%, #333333 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

const WolfHead = styled.img`
  width: 96px;
  height: 96px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 2rem 0;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  padding: 12px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  text-align: center;
`;

const SsoButton = styled.button`
  padding: 12px;
  background: white;
  color: #1e3a8a;
  border: 1.5px solid #1e3a8a;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: #1e3a8a;
    color: white;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0.5rem 0;

  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
  }

  &::before { margin-right: 0.75rem; }
  &::after  { margin-left: 0.75rem; }
`;

const SSO_ERROR_MESSAGES = {
  not_provisioned: "This Okta account isn't set up for ocr-matching. Ask an admin to add you.",
  inactive: 'Your account is disabled. Contact an admin.',
  sso_failed: 'Sign-in with Okta failed. Try again or use password.',
  okta_denied: 'Okta declined the sign-in. Contact IT.',
  sso_disabled: 'SSO is currently disabled. Use password.',
};

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoConfig, setSsoConfig] = useState(null);
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('error');
  const ssoErrorMessage = ssoError && SSO_ERROR_MESSAGES[ssoError];

  useEffect(() => {
    getSsoConfig()
      .then(setSsoConfig)
      .catch(() => setSsoConfig({ sso_enabled: false }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(username, password);
    
    setIsLoading(false);
    
    if (result.success) {
      // Redirect to the page they were trying to access, or home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <LogoIcon>
            <WolfHead src="/wolf-head.png" alt="Wolf Head Logo" />
          </LogoIcon>
        </Logo>
        
        <Title>Verisure</Title>
        <Subtitle>A WOLF DATA SOLUTION</Subtitle>
        
        <Form onSubmit={handleSubmit}>
          {ssoErrorMessage && <ErrorMessage>{ssoErrorMessage}</ErrorMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {ssoConfig?.sso_enabled && (
            <>
              <SsoButton
                type="button"
                onClick={() => { window.location.href = getSsoStartUrl('/'); }}
              >
                Sign in with {ssoConfig.provider_name || 'Okta'}
              </SsoButton>
              <Divider>or use password</Divider>
            </>
          )}

          <FormGroup>
            <Label htmlFor="username">Email / Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter your email or username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;

